// Problem-code → user copy for the module-06 office screens.
//
// Every module-06 refusal is an RFC 9457 problem with a stable UPPER_SNAKE
// `code` (spec 06 §5). The screens translate the codes they can actually
// provoke into an actionable sentence; anything else falls back to the server's
// own `title`, which is already human text — a raw code must never reach a
// user, and neither must a generic "something went wrong" that hides a
// perfectly clear server explanation.

import type { TFunction } from "i18next";
import { RequestError } from "../../../../lib/request";

/**
 * The codes these screens can raise. Keeping the list explicit (rather than
 * `t(code)` on anything) means an unknown code degrades to the server title
 * instead of rendering a missing-key placeholder.
 */
export const TRANSLATED_PROBLEM_CODES = new Set([
  "MOVEMENT_ILLEGAL",
  "INSUFFICIENT_REMAINING",
  "INSUFFICIENT_ATP",
  "UOM_NOT_CONVERTIBLE",
  "NEGATIVE_STOCK_BLOCKED",
  "LOCATION_HIERARCHY_INVALID",
  "VIRTUAL_LOCATION_IMMUTABLE",
  "LOCATION_CODE_TAKEN",
  "LOCATION_NOT_COUNTABLE",
  "ALLOW_NEGATIVE_PRODUCTION_ONLY",
  "MEASURE_PRECISION_LOSS",
  "RESERVATION_CONFLICT",
  "RESERVATION_INVALID",
  "LOT_NOT_RELEASED",
  "LOT_STATE_INVALID",
  "TRANSFER_STATE_INVALID",
  "COUNT_STATE_INVALID",
  "MOVEMENT_ALREADY_REVERSED",
  "REVERSAL_NOT_REVERSIBLE",
  "ADJUSTMENT_APPROVAL_REQUIRED",
  "ADJUSTMENT_REQUEST_STALE_STOCK",
  "UNIT_NOT_AVAILABLE",
  "CUT_NO_PIECES",
  "CUT_PIECE_NOT_POSITIVE",
  "CUT_SOURCE_NEGATIVE",
  "CUT_SERIAL_TAKEN",
  "CUT_TOLERANCE_INVALID",
  "CUT_FAULT_OUT_OF_RANGE",
  "RESOURCE_BUSY",
  // Idempotency + queue codes: reachable only through the floor outbox, but the
  // table is the MODULE's, not a screen's, so they live here with the rest.
  "IDEMPOTENCY_KEY_REUSE",
  "IDEMPOTENCY_IN_PROGRESS",
  "IDEMPOTENCY_KEY_REQUIRED",
  "TRANSFER_VARIANCE_REASON_REQUIRED",
  "CUT_EXCEEDS_REMAINING",
  "PERMISSION_DENIED",
  // §4.12's own refusals (D-315): a prefill id that went away, a request
  // someone else settled first.
  "LOT_NOT_FOUND",
  "LOCATION_NOT_FOUND",
  "PRODUCT_NOT_FOUND",
  "ADJUSTMENT_REQUEST_STATE_INVALID",
  "ADJUSTMENT_REQUEST_NOT_FOUND",
  // P3-H-3 (D-391 ruling 3): the work order an adjustment is recorded against
  // is gone, or module 04 is not there to confirm it.
  "WORK_ORDER_NOT_FOUND",
  // §5 row 5's location label sheet (D-343): every refusal the
  // route names. A generic tenant met QR_LABEL_TEMPLATE_NOT_FOUND as an
  // untranslated English title until its pack gained a location template.
  "QR_LABEL_TEMPLATE_NOT_FOUND",
  "PACK_NOT_INSTALLED",
  "LABEL_PRINTER_UNAVAILABLE",
  "QR_PAYLOAD_MISSING",
  "QR_KIT_SELECTION_EMPTY",
]);

/**
 * The sentence to show for a failed mutation. Order: a translated code, then
 * the server's problem title, then the caller's generic fallback.
 */
export function inventoryErrorMessage(
  t: TFunction,
  error: unknown,
  fallbackKey = "inventory.errors.generic",
): string {
  if (error instanceof RequestError) {
    const code = error.code;
    if (code !== undefined && TRANSLATED_PROBLEM_CODES.has(code)) {
      return t(`inventory.errors.${code}`, { defaultValue: error.message });
    }
    if (error.message.length > 0) return error.message;
  }
  return t(fallbackKey);
}

/** Extra problem fields the §9 contracts promise (fresh remaining, ATP…). */
export function problemExtension(error: unknown, key: string): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  const problem = error.problem as Record<string, unknown> | undefined;
  const value = problem?.[key];
  return typeof value === "string" ? value : undefined;
}
